import {
  Sidebar,
  SidebarContent,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@/components/ui/sidebar';
import { useAuth } from '@/hooks/auth.hook';
import { useRootSelector } from '@/hooks/selector.hook';
import { useLocation, Link } from 'react-router-dom';
import mockAvatar from '@/assets/images/mockAvatar.png';
import NexgenBrosLogo from '@/assets/svg/LogoLandscape.svg';
import NexgenBrosLogoDark from '@/assets/svg/LogoLandscapeDark.svg';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '../ui/button';
import { LogOutIcon } from './icon';
import * as Icons from './icon';


export const AppSidebar = () => {
  const location = useLocation();
  const { logout } = useAuth();
  const { user } = useRootSelector((state) => state.auth);
  const { dataSidebar } = useRootSelector((state) => state.sidebar);
  const { isMobile, setOpenMobile } = useSidebar();

  // Find which item should be active based on url
  // If no match is found, default to the first item (index 0)
  const activeItemIndex = dataSidebar.navigation.findIndex((item) => {
    if (item.url === '/' && location.pathname === '/') return true;
    if (item.url !== '/' && location.pathname.startsWith(item.url)) return true;
    return false;
  });
  const defaultActiveIndex = activeItemIndex !== -1 ? activeItemIndex : 0;

  // Function to handle sidebar item click on mobile
  const handleSidebarItemClick = () => {
    if (isMobile) {
      setOpenMobile(false);
    }
  };

  const getIconForTitle = (title: string) => {
    switch (title) {
      case 'Stores':
        return Icons.StoresIcon;
      case 'Orders':
        return Icons.OrdersIcon;
      case 'Departments':
        return Icons.DepartmentsIcon;
      case 'Roles':
        return Icons.RolesIcon;
      case 'Functions':
        return Icons.FunctionsIcon;
      case 'Permission':
      case 'Permissions':
        return Icons.PermissionIcon;
      case 'User':
      case 'Users':
        return Icons.UserIcon;
      case 'Sheet':
      case 'Sheets':
        return Icons.SheetIcon;
      case 'Fulfillment':
      case 'Fulfillments':
        return Icons.OrdersIcon;
      case 'Teams':
        return Icons.TeamsIcon;
      case 'Settings':
        return Icons.SettingsIcon;
      case 'Factories':
        return Icons.FactoriesIcon;
      default:
        return Icons.StoresIcon;
    }
  };

  return (
    <Sidebar collapsible="icon" className="bg-box h-screen border-r border-main">
      <SidebarContent className="!gap-0 !p-0">
        <div className="flex flex-col h-full bg-box">
          {/* Logo */}
          <div className="flex-shrink-0">
            <img
              src={NexgenBrosLogo}
              alt="Nexgen Bros"
              className="pt-[2rem] lg:pt-[3.3rem] dark:hidden pb-[1rem] lg:pb-[1.3rem] border-b border-main px-[2.4rem] w-full"
            />
            <img
              src={NexgenBrosLogoDark}
              alt="Nexgen Bros"
              className="pt-[2rem] lg:pt-[3.3rem] dark:block hidden pb-[1rem] lg:pb-[1.3rem] border-b border-main px-[2.4rem] w-full"
            />
          </div>

          {/* Menu Items - Scrollable */}
          <div className="flex-1 overflow-y-auto overflow-x-hidden overscroll-contain px-[2.4rem] pt-[1.2rem] lg:pt-[1.8rem] pb-[0.4rem]">
            <SidebarMenu className="flex flex-col gap-[0.8rem] lg:gap-[1.2rem]">
              {dataSidebar.navigation.map((item, index) => {
                const itemIsActive = index === defaultActiveIndex;
                const IconComponent = getIconForTitle(item.title);

                return (
                  <SidebarMenuItem key={item.url}>
                    <SidebarMenuButton
                      asChild
                      isActive={itemIsActive}
                      className="px-[1.6rem] data-[active=true]:bg-interactive data-[active=true]:text-[var(--color-background-action-primary)] py-[0.8rem] lg:py-[1.2rem] h-[4rem] lg:h-[4.8rem] rounded-[1rem] font-normal text-[1.6rem] leading-[2.4rem] tracking-[0px] text-center"
                    >
                      <Link to={item.url} onClick={handleSidebarItemClick}>
                        <IconComponent className="!h-[2rem] !w-[2rem]" />
                        {item.title}
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </div>

          {/* Profile & Logout - Always visible at bottom */}
          <div className="flex-shrink-0 border-t border-main">
            <div className="px-[1.6rem] py-[0.8rem] lg:py-[1.2rem] flex gap-[1.2rem] items-center">
              <Avatar className="w-[3rem] h-[3rem] lg:w-[3.5rem] lg:h-[3.5rem]">
                <AvatarImage src={user?.avatar ? user?.avatar : mockAvatar} alt="Bong-chan" />
                <AvatarFallback>{user?.userName}</AvatarFallback>
              </Avatar>
              <div className="flex flex-col gap-[0.3rem] lg:gap-[0.5rem]">
                <p className="font-medium text-[1.3rem] lg:text-[1.4rem] leading-5">
                  {user?.fullName}
                </p>
                <p className="font-roboto text-[1.2rem] leading-4 sub-text">{user?.email}</p>
              </div>
            </div>

            <div>
              <Button
                onClick={() => logout()}
                className="!bg-transparent text-normal w-full text-[1.6rem] gap-[1.2rem] !h-auto py-[1rem] lg:py-[1.4rem] px-[1.6rem] !justify-start flex items-center leading-6 text-center rounded-[1rem] hover:bg-interactive hover:text-[var(--color-background-action-primary)] transition-colors"
              >
                <LogOutIcon className="!h-[2rem] !w-[2rem]" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </SidebarContent>
    </Sidebar>
  );
};
